import type { ReactNode } from "react";
import { Sigma, XIcon } from "lucide-react";

import { cellChipLabel, type KoryCellContext } from "@/lib/kory-cell-context";

/**
 * The folded tray's word for the values you captured: "1 value", which opens the tray to them, and an ×
 * that lets them go. The open tray's header shows each value in full, so this chip is the folded line's.
 */
export function AttachedValueChip({
  cells,
  onOpen,
  onClear,
}: {
  cells: KoryCellContext[];
  onOpen: () => void;
  onClear: () => void;
}) {
  const one = cells.length === 1;
  const text = valueCount(cells.length);
  return (
    <span
      data-testid="kory-attached-value-chip"
      className="inline-flex h-7 shrink-0 items-center rounded-full border bg-secondary text-xs font-medium text-secondary-foreground"
    >
      {/* Each hit area reaches past the chip, so a thumb finds it without a taller chip. */}
      <button
        type="button"
        aria-label={`${text} attached for Kory. Open to see ${one ? "it" : "them"}`}
        title={cells.map(cellChipLabel).join("\n")}
        onClick={onOpen}
        className="relative inline-flex h-full items-center gap-1 rounded-l-full pr-1 pl-2.5 whitespace-nowrap after:absolute after:-inset-y-2 after:inset-x-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <Sigma className="size-3.5 shrink-0" aria-hidden />
        {text}
      </button>
      <button
        type="button"
        aria-label={one ? "Remove the attached value from Kory" : `Remove the ${cells.length} attached values from Kory`}
        onClick={onClear}
        className="relative grid h-full w-7 place-items-center rounded-r-full after:absolute after:-inset-y-2 after:inset-x-0 hover:bg-foreground/10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <XIcon className="size-3" aria-hidden />
      </button>
    </span>
  );
}

function valueCount(n: number): string {
  return `${n} value${n === 1 ? "" : "s"}`;
}

/**
 * The docked rail's Kory mark, folded: a count on the mark while values are attached, and the rail's
 * name and hover list for them, in the same words as the open tray's "What Kory sees".
 */
export function attachedValueRail(mark: ReactNode, cells: KoryCellContext[]): { mark: ReactNode; name?: string; hint?: ReactNode } {
  if (!cells.length) return { mark };
  return {
    mark: (
      <span className="relative inline-flex">
        {mark}
        <span
          data-testid="kory-rail-value-count"
          aria-hidden
          className="absolute -top-1 right-0 z-10 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-primary px-1 text-[11px] leading-none font-semibold text-primary-foreground tabular-nums ring-2 ring-background"
        >
          {cells.length}
        </span>
      </span>
    ),
    name: `Open Kory. ${valueCount(cells.length)} attached`,
    hint: (
      <div data-testid="kory-rail-value-list" className="max-w-80 text-left text-pretty">
        <p className="font-medium">
          {valueCount(cells.length)} {cells.length === 1 ? "goes" : "go"} with your next question
        </p>
        <ul className="mt-1 space-y-0.5">
          {cells.map((cell) => (
            <li key={cell.id} className="flex gap-1">
              <Sigma className="mt-0.5 size-3 shrink-0" aria-hidden />
              <span>{cellChipLabel(cell)}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  };
}
